import React, { useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { CalendarIcon, Plus, X, MessageCircle, Coins, Mail } from "lucide-react";
import { format } from "date-fns";
import { getFormattedDateTime } from "@/utils";
import { cn } from "@/lib/utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { CampaignApi } from "@/api";
import type { MessageConfigPayload } from "@/api/campaign.api";
import { HandleError } from "@/api/axiosClient";

function createCampaignFormSchema(giftOs: boolean) {
  return z
    .object({
      campaignName: z.string().min(1, { message: "Campaign name is required" }),
      startDate: z.date({
        required_error: "Start date is required",
      }),
      endDate: z.date({
        required_error: "End date is required",
      }),
      campaignType: giftOs
        ? z.literal("only-message")
        : z.enum(["only-message", "prepaid-points"], {
            required_error: "Please select a campaign type",
          }),
      loadBonusPoint: z.string().optional(),
      scheduleMessage: giftOs
        ? z.enum(["sms", "whatsapp", "email"], {
            required_error: "Please select a channel",
          }).optional()
        : z
            .enum(["sms", "whatsapp"], {
              required_error: "Please select a message type",
            })
            .optional(),
      messageType: z.string().optional(),
      template: z.string().optional(),
      sendDate: z.date().optional(),
    })
    .refine(
      (data) => {
        if (!giftOs && data.campaignType === "prepaid-points") {
          return data.loadBonusPoint && data.loadBonusPoint.trim() !== "";
        }
        return true;
      },
      {
        message:
          "Load Bonus Point is required for Prepaid Points Based campaigns",
        path: ["loadBonusPoint"],
      }
    )
    .refine(
      (data) => {
        return data.endDate >= data.startDate;
      },
      {
        message: "End date must be after start date",
        path: ["endDate"],
      }
    );
}

type CampaignFormValues = z.infer<ReturnType<typeof createCampaignFormSchema>>;

export interface CampaignCreateProps {
  /** Gift OS uses a simplified flow: no campaign type, Email in schedule, no message type (auto-assigned). */
  variant?: "default" | "gift-os";
}

export function CampaignCreate({ variant = "default" }: CampaignCreateProps) {
  const giftOs = variant === "gift-os";
  const campaignFormSchema = useMemo(
    () => createCampaignFormSchema(giftOs),
    [giftOs]
  );
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  const [sendDateOpen, setSendDateOpen] = useState(false);
  // Holds all message configurations that will be sent as listMsgConfig in the API payload
  const [messageConfigs, setMessageConfigs] = useState<MessageConfigPayload[]>(
    []
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm<CampaignFormValues>({
    resolver: zodResolver(campaignFormSchema),
    defaultValues: {
      campaignName: "",
      startDate: undefined,
      endDate: undefined,
      campaignType: giftOs ? "only-message" : undefined,
      loadBonusPoint: "",
      scheduleMessage: undefined,
      messageType: "",
      template: "",
      sendDate: undefined,
    },
  });

  const campaignType = form.watch("campaignType");
  const scheduleMessage = form.watch("scheduleMessage");
  const messageType = form.watch("messageType");
  const template = form.watch("template");
  const selectedTemplate = template;

  // Sample template scripts (will be replaced with API data later)
  const templateScripts: Record<string, string> = {
    template1:
      "Welcome to our campaign! You have been selected for exclusive rewards.",
    template2: "Don't miss out! Your campaign benefits are waiting for you.",
    template3: "Congratulations! You've successfully enrolled in our campaign.",
  };

  const templateIdMap: Record<string, number> = {
    template1: 1001,
    template2: 1002,
    template3: 1003,
  };

  const onStep1Next = async () => {
    const step1Fields: (keyof CampaignFormValues)[] = giftOs
      ? ["campaignName", "startDate", "endDate"]
      : ["campaignName", "startDate", "endDate", "campaignType", "loadBonusPoint"];
    const isValid = await form.trigger(step1Fields);

    if (isValid) {
      setStep(2);
    }
  };

  const onStep2Next = async () => {
    // Require at least one message configuration before moving to Preview
    if (messageConfigs.length === 0) {
      toast({
        title: "Error",
        description: "Please add at least one message configuration.",
        variant: "destructive",
      });
      return;
    }

    setStep(3);
  };

  // Add a new message configuration based on current Step 2 form fields
  const handleAddMessageConfig = () => {
    const scheduleMessage = form.getValues("scheduleMessage");
    const messageType = form.getValues("messageType");
    const template = form.getValues("template");
    const sendDate = form.getValues("sendDate");

    if (giftOs) {
      if (!scheduleMessage || !template || !sendDate) {
        toast({
          title: "Error",
          description:
            "Please select schedule channel, template, and send date before adding.",
          variant: "destructive",
        });
        return;
      }

      const hasIntro = messageConfigs.some(
        (cfg) => cfg.messageType === "Introduction"
      );
      let messageTypeForPayload: string;
      if (!hasIntro) {
        messageTypeForPayload = "Introduction";
      } else {
        const existingReminders = messageConfigs.filter((cfg) =>
          String(cfg.messageType).startsWith("Reminder")
        ).length;
        messageTypeForPayload = `Reminder${existingReminders + 1}`;
      }

      const communicationType: MessageConfigPayload["communicationType"] =
        scheduleMessage === "sms"
          ? "SMS"
          : scheduleMessage === "whatsapp"
          ? "WhatsApp"
          : "Email";

      const templateId = templateIdMap[template] ?? 0;

      const newConfig: MessageConfigPayload = {
        communicationType,
        messageType: messageTypeForPayload,
        templateId,
        messageSentDate: sendDate.toISOString(),
        messageText: templateScripts[template] || "",
      };

      setMessageConfigs((prev) => [...prev, newConfig]);
      form.setValue("scheduleMessage", undefined);
      form.setValue("template", "");
      form.setValue("sendDate", undefined);

      toast({
        title: "Added",
        description: "Message configuration added successfully.",
      });
      return;
    }

    if (!scheduleMessage || !messageType || !template || !sendDate) {
      toast({
        title: "Error",
        description: "Please fill all Message Configuration fields before adding.",
        variant: "destructive",
      });
      return;
    }

    // Enforce: only one Introduction and one Success configuration
    const normalizedType =
      messageType === "introduction"
        ? "Introduction"
        : messageType === "reminder"
        ? "Reminder"
        : "Success";

    if (
      (normalizedType === "Introduction" || normalizedType === "Success") &&
      messageConfigs.some((cfg) => cfg.messageType === normalizedType)
    ) {
      toast({
        title: "Error",
        description: `Only one ${normalizedType} message configuration is allowed.`,
        variant: "destructive",
      });
      return;
    }

    const communicationType: MessageConfigPayload["communicationType"] =
      scheduleMessage === "sms" ? "SMS" : "WhatsApp";

    const templateId = templateIdMap[template] ?? 0;

    // For reminders, backend expects Reminder1, Reminder2, ...
    let messageTypeForPayload: string = normalizedType;
    if (normalizedType === "Reminder") {
      const existingReminders = messageConfigs.filter(
        (cfg) => cfg.messageType.startsWith("Reminder")
      ).length;
      messageTypeForPayload = `Reminder${existingReminders + 1}`;
    }

    const newConfig: MessageConfigPayload = {
      communicationType,
      messageType: messageTypeForPayload,
      templateId,
      messageSentDate: sendDate.toISOString(),
      messageText: templateScripts[template] || "",
    };

    setMessageConfigs((prev) => [...prev, newConfig]);

    // Clear Step 2 fields so user can easily add another configuration
    form.setValue("scheduleMessage", undefined);
    form.setValue("messageType", "");
    form.setValue("template", "");
    form.setValue("sendDate", undefined);

    toast({
      title: "Added",
      description: "Message configuration added successfully.",
    });
  };

  const handleRemoveMessageConfig = (index: number) => {
    setMessageConfigs((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-gray-800">Create Campaign</h2>
        <p className="text-sm text-muted-foreground">
          Create a new campaign for your loyalty program
        </p>
      </div>

      {/* Step Indicator */}
      <div className="flex items-center justify-center gap-4 mb-6">
        <div
          className={`flex items-center gap-2 ${
            step === 1 ? "font-bold text-blue-600" : "text-gray-400"
          }`}
        >
          1 <span className="hidden md:inline">Campaign Details</span>
        </div>
        <div
          className={`h-0.5 w-8 ${step > 1 ? "bg-blue-600" : "bg-gray-200"}`}
        ></div>
        <div
          className={`flex items-center gap-2 ${
            step === 2 ? "font-bold text-blue-600" : "text-gray-400"
          }`}
        >
          2 <span className="hidden md:inline">Message Configuration</span>
        </div>
        <div
          className={`h-0.5 w-8 ${step > 2 ? "bg-blue-600" : "bg-gray-200"}`}
        ></div>
        <div
          className={`flex items-center gap-2 ${
            step === 3 ? "font-bold text-blue-600" : "text-gray-400"
          }`}
        >
          3 <span className="hidden md:inline">Preview</span>
        </div>
      </div>

      <Form {...form}>
        <form className="space-y-6">
          {step === 1 && (
            <Card>
              <CardHeader>
                <CardTitle>Step 1: Campaign Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Campaign Name */}
                <FormField
                  control={form.control}
                  name="campaignName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Campaign Name</FormLabel>
                      <FormControl>
                        <Input placeholder="Enter campaign name" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Start Date */}
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Start Date</FormLabel>
                      <Popover
                        open={startDateOpen}
                        onOpenChange={setStartDateOpen}
                      >
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                              setStartDateOpen(false);
                            }}
                            disabled={(date) =>
                              date < new Date(new Date().setHours(0, 0, 0, 0))
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* End Date */}
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>End Date</FormLabel>
                      <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                              setEndDateOpen(false);
                            }}
                            disabled={(date) => {
                              const startDate = form.getValues("startDate");
                              return (
                                date <
                                  new Date(new Date().setHours(0, 0, 0, 0)) ||
                                (startDate && date < startDate)
                              );
                            }}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Campaign Type (Perk Point only) */}
                {!giftOs && (
                  <FormField
                    control={form.control}
                    name="campaignType"
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel>Campaign Type</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="grid grid-cols-1 md:grid-cols-2 gap-4"
                          >
                            {/* Only Message Card */}
                            <label
                              htmlFor="only-message"
                              className={cn(
                                "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors",
                                field.value === "only-message"
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300"
                              )}
                            >
                              <RadioGroupItem
                                value="only-message"
                                id="only-message"
                                className="sr-only"
                              />
                              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-100 text-blue-600">
                                <MessageCircle className="h-5 w-5" />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold">
                                  Only Message
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  Send communication without loading points.
                                </span>
                              </div>
                            </label>

                            {/* Prepaid Points Based Card */}
                            <label
                              htmlFor="prepaid-points"
                              className={cn(
                                "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors",
                                field.value === "prepaid-points"
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-gray-300"
                              )}
                            >
                              <RadioGroupItem
                                value="prepaid-points"
                                id="prepaid-points"
                                className="sr-only"
                              />
                              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-100 text-emerald-600">
                                <Coins className="h-5 w-5" />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold">
                                  Prepaid Points Based
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  Load bonus points as part of the campaign.
                                </span>
                              </div>
                            </label>
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Load Bonus Point - Conditional */}
                {!giftOs && campaignType === "prepaid-points" && (
                  <FormField
                    control={form.control}
                    name="loadBonusPoint"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Load Bonus Point</FormLabel>
                        <FormControl>
                          <Input
                            type="number"
                            placeholder="Enter bonus points"
                            {...field}
                            min="0"
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || Number(value) >= 0) {
                                field.onChange(value);
                              }
                            }}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Next Button */}
                <div className="flex justify-end">
                  <Button type="button" onClick={onStep1Next}>
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 2 && (
            <Card>
              <CardHeader>
                <CardTitle>Step 2: Message Configuration</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Schedule Message */}
                <FormField
                  control={form.control}
                  name="scheduleMessage"
                  render={({ field }) => (
                    <FormItem className="space-y-3">
                      <FormLabel>Schedule Message</FormLabel>
                      <FormControl>
                        <RadioGroup
                          onValueChange={field.onChange}
                          value={field.value}
                          className={cn(
                            "grid gap-4",
                            giftOs
                              ? "grid-cols-1 md:grid-cols-3"
                              : "grid-cols-1 md:grid-cols-2"
                          )}
                        >
                          {/* SMS Card */}
                          <label
                            htmlFor="sms"
                            className={cn(
                              "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors",
                              field.value === "sms"
                                ? "border-blue-500 bg-blue-50"
                                : "border-gray-200 hover:border-gray-300"
                            )}
                          >
                            <RadioGroupItem
                              value="sms"
                              id="sms"
                              className="sr-only"
                            />
                            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-blue-100 text-blue-600">
                              <MessageCircle className="h-5 w-5" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold">SMS</span>
                              <span className="text-xs text-muted-foreground">
                                Send campaign updates via SMS.
                              </span>
                            </div>
                          </label>

                          {/* WhatsApp Card */}
                          <label
                            htmlFor="whatsapp"
                            className={cn(
                              "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors",
                              field.value === "whatsapp"
                                ? "border-green-500 bg-emerald-50"
                                : "border-gray-200 hover:border-gray-300"
                            )}
                          >
                            <RadioGroupItem
                              value="whatsapp"
                              id="whatsapp"
                              className="sr-only"
                            />
                            <div className="flex h-10 w-10 items-center justify-center rounded-md bg-emerald-100 text-emerald-600">
                              <MessageCircle className="h-5 w-5" />
                            </div>
                            <div className="flex flex-col">
                              <span className="text-sm font-semibold">
                                WhatsApp
                              </span>
                              <span className="text-xs text-muted-foreground">
                                Reach customers directly on WhatsApp.
                              </span>
                            </div>
                          </label>

                          {/* Email (Gift OS only) */}
                          {giftOs && (
                            <label
                              htmlFor="email"
                              className={cn(
                                "flex items-center gap-3 p-4 border rounded-lg cursor-pointer transition-colors",
                                field.value === "email"
                                  ? "border-violet-500 bg-violet-50"
                                  : "border-gray-200 hover:border-gray-300"
                              )}
                            >
                              <RadioGroupItem
                                value="email"
                                id="email"
                                className="sr-only"
                              />
                              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-violet-100 text-violet-700">
                                <Mail className="h-5 w-5" />
                              </div>
                              <div className="flex flex-col">
                                <span className="text-sm font-semibold">Email</span>
                                <span className="text-xs text-muted-foreground">
                                  Send campaign updates via email.
                                </span>
                              </div>
                            </label>
                          )}
                        </RadioGroup>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Message Type (Perk Point only; Gift OS assigns types automatically) */}
                {!giftOs && (
                  <FormField
                    control={form.control}
                    name="messageType"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Message Type</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select message type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="introduction">
                              Introduction
                            </SelectItem>
                            <SelectItem value="reminder">Reminder</SelectItem>
                            <SelectItem value="success">Success</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {/* Template */}
                <FormField
                  control={form.control}
                  name="template"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Template</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select template" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {/* Templates will be added later */}
                          <SelectItem value="template1">Template 1</SelectItem>
                          <SelectItem value="template2">Template 2</SelectItem>
                          <SelectItem value="template3">Template 3</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Send Date */}
                <FormField
                  control={form.control}
                  name="sendDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Send Date</FormLabel>
                      <Popover
                        open={sendDateOpen}
                        onOpenChange={setSendDateOpen}
                      >
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, "PPP")
                              ) : (
                                <span>Pick a date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={(date) => {
                              field.onChange(date);
                              setSendDateOpen(false);
                            }}
                            disabled={(date) =>
                              date < new Date(new Date().setHours(0, 0, 0, 0))
                            }
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* Template Preview */}
                {selectedTemplate && (
                  <div className="space-y-2">
                    <FormLabel>Template Preview</FormLabel>
                    <div className="p-4 border rounded-md bg-gray-50 min-h-[100px]">
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">
                        {templateScripts[selectedTemplate] ||
                          "Template script will be shown here for selected template."}
                      </p>
                    </div>
                  </div>
                )}

                {/* Add Message Configuration button */}
                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={handleAddMessageConfig}>
                    Add Message Configuration
                  </Button>
                </div>

                {/* Preview of added message configurations */}
                {messageConfigs.length > 0 && (
                  <div className="space-y-3 border-t pt-4">
                    <h3 className="text-md font-semibold">Configured Messages</h3>
                    <div className="space-y-3">
                      {messageConfigs.map((cfg, index) => (
                        <div
                          key={index}
                          className="flex items-start justify-between p-4 border rounded-md bg-gray-50"
                        >
                          <div className="space-y-1 text-sm">
                            <p className="font-medium">
                              #{index + 1} {cfg.messageType} ({cfg.communicationType})
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Template ID:</span>{" "}
                              {cfg.templateId}
                            </p>
                            <p className="text-gray-600">
                              <span className="font-semibold">Send Date:</span>{" "}
                              {getFormattedDateTime(cfg.messageSentDate)}
                            </p>
                            {cfg.messageText && (
                              <p className="text-gray-600 whitespace-pre-wrap">
                                <span className="font-semibold">Message Text:</span>{" "}
                                {cfg.messageText}
                              </p>
                            )}
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveMessageConfig(index)}
                            className="text-red-600 hover:text-red-700"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Next Button */}
                <div className="flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(1)}
                  >
                    Previous
                  </Button>
                  <Button type="button" onClick={onStep2Next}>
                    Next
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {step === 3 && (
            <Card>
              <CardHeader>
                <CardTitle>Preview: Review & Submit</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Campaign Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-500">
                        Campaign Name
                      </p>
                      <p className="text-sm">
                        {form.getValues("campaignName") || "-"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-500">
                        Campaign Type
                      </p>
                      <p className="text-sm">
                        {form.getValues("campaignType") === "only-message"
                          ? "Only Message"
                          : form.getValues("campaignType") === "prepaid-points"
                          ? "Prepaid Points Based"
                          : "-"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-500">
                        Start Date
                      </p>
                      <p className="text-sm">
                        {form.getValues("startDate")
                          ? getFormattedDateTime(form.getValues("startDate"))
                          : "-"}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm font-medium text-gray-500">
                        End Date
                      </p>
                      <p className="text-sm">
                        {form.getValues("endDate")
                          ? getFormattedDateTime(form.getValues("endDate"))
                          : "-"}
                      </p>
                    </div>
                    {!giftOs &&
                      form.getValues("campaignType") === "prepaid-points" && (
                      <div className="space-y-1">
                        <p className="text-sm font-medium text-gray-500">
                          Load Bonus Point
                        </p>
                        <p className="text-sm">
                          {form.getValues("loadBonusPoint") || "-"}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                <div className="border-t pt-4 space-y-4">
                  <h3 className="text-lg font-semibold">
                    Message Configuration
                  </h3>
                  {messageConfigs.length === 0 ? (
                    <p className="text-sm text-gray-500">
                      No message configurations added.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {messageConfigs.map((cfg, index) => (
                        <div
                          key={index}
                          className="p-4 border rounded-md bg-gray-50 space-y-1"
                        >
                          <p className="text-sm font-medium text-gray-700">
                            #{index + 1} {cfg.messageType} ({cfg.communicationType})
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold">Template ID:</span>{" "}
                            {cfg.templateId}
                          </p>
                          <p className="text-sm text-gray-600">
                            <span className="font-semibold">Send Date:</span>{" "}
                            {getFormattedDateTime(cfg.messageSentDate)}
                          </p>
                          {cfg.messageText && (
                            <p className="text-sm text-gray-600 whitespace-pre-wrap">
                              <span className="font-semibold">Message Text:</span>{" "}
                              {cfg.messageText}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {/* Action Buttons */}
                <div className="flex justify-end gap-2 pt-4 border-t">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setStep(2)}
                  >
                    Previous
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      form.reset();
                      setStep(1);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    disabled={isSubmitting}
                    onClick={async () => {
                      const isValid = await form.trigger();
                      if (!isValid) return;

                      if (messageConfigs.length === 0) {
                        toast({
                          title: "Error",
                          description:
                            "Please add at least one message configuration before submitting.",
                          variant: "destructive",
                        });
                        return;
                      }

                      try {
                        setIsSubmitting(true);
                        const values = form.getValues();

                        const campaignTypeApi: "MessageOnly" | "PrepaidPointBase" =
                          values.campaignType === "only-message"
                            ? "MessageOnly"
                            : "PrepaidPointBase";

                        const payload = {
                          campaignDetails: {
                            p_Id: 0,
                            p_CampaignName: values.campaignName,
                            p_StartDate: values.startDate!.toISOString(),
                            p_EndDate: values.endDate!.toISOString(),
                            p_CampaignType: campaignTypeApi,
                            p_BonusPoints:
                              !giftOs &&
                              values.campaignType === "prepaid-points"
                                ? Number(values.loadBonusPoint || 0)
                                : 0,
                            // TODO: Replace with actual logged-in user id when available
                            p_CreatedBy: 1,
                            p_UpdatedBy: 0,
                          },
                          listMsgConfig: messageConfigs,
                        };

                        console.log(
                          "CreateUpdateCampaign payload:",
                          JSON.stringify(payload, null, 2)
                        );

                        if (giftOs) {
                          toast({
                            title: "Success",
                            description: "Campaign created successfully!",
                          });
                          form.reset();
                          setMessageConfigs([]);
                          setStep(1);
                          return;
                        }

                        const response = await CampaignApi.createUpdateCampaign(
                          payload
                        );

                        if (response.error_msg && response.error_msg.trim() !== "") {
                          toast({
                            title: "Error",
                            description: response.error_msg,
                            variant: "destructive",
                          });
                        } else {
                          toast({
                            title: "Success",
                            description:
                              response.message || "Campaign created successfully!",
                          });
                          // Reset form and local state after successful submission
                          form.reset();
                          setMessageConfigs([]);
                          setStep(1);
                        }
                      } catch (error) {
                        console.error("Error creating campaign:", error);
                        const errorMessage = HandleError(error);
                        toast({
                          title: "Error",
                          description:
                            errorMessage || "Failed to create or update campaign",
                          variant: "destructive",
                        });
                      } finally {
                        setIsSubmitting(false);
                      }
                    }}
                  >
                    {isSubmitting ? "Submitting..." : "Submit"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </form>
      </Form>

      {/* Test Message Modal removed as per requirements */}
    </div>
  );
}
